import { PermissionCheck } from '@/components';
import { useAppSelector } from '@/state';
import { ReleasesApi } from '@/state/api';
import { permissionsKeys } from '@/types/permissionsType';
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Input,
  Stack,
  Table,
  Text,
} from '@chakra-ui/react';
import { OverlayDialog, toasterApi } from '@ui';
import { useState } from 'react';
import ManageCard from '../ManageCard';

const statusColor = (status?: string) => {
  if (status === 'ACTIVE') return 'green';
  if (status === 'PROCESSING') return 'blue';
  if (status === 'FAILED') return 'red';
  if (status === 'ARCHIVED') return 'gray';
  return 'orange';
};

const BomImports = () => {
  const project = useAppSelector((state) => state.project.selectedProject);
  const [replacementFor, setReplacementFor] = useState<
    ReleasesApi.Responses['getAllRelease'][number] | null
  >(null);
  const { data: releases = [], isLoading } =
    ReleasesApi.Api.useGetAllReleaseQuery({
      project: project.id ?? undefined,
      include_inactive: true,
      include_dependencies: true,
    });
  const { data: audit = [] } = ReleasesApi.Api.useGetBomAuditQuery({
    project: project.id ?? undefined,
  });
  const [archiveRelease, { isLoading: isArchiving }] =
    ReleasesApi.Api.useArchiveReleaseMutation();

  const archive = (release: (typeof releases)[number]) => {
    const dependencies = Object.entries(
      release.dependency_summary ?? {},
    ).filter(([_key, count]) => count > 0);
    OverlayDialog.open(`archive-bom-${release.id}`, {
      overlayId: `archive-bom-${release.id}`,
      title: `Archive BOM ${release.number}`,
      description:
        `Archive ${release.bom_file_name || release.number} from project ` +
        `${project.item?.name || project.id}?`,
      type: 'warning',
      content: (
        <Stack gap={3} align="stretch">
          <Text>
            Release: <b>{release.number}</b> · Project:{' '}
            <b>{project.item?.name || project.id}</b>
          </Text>
          <Text color="fg.muted" fontSize="sm">
            It will disappear from active receiving and production selectors.
            Existing receipts, inspections, stock, purchase orders, and work
            orders remain intact and readable.
          </Text>
          {dependencies.length > 0 && (
            <Text fontSize="sm">
              Preserved references:{' '}
              {dependencies
                .map(([name, count]) => `${name.replaceAll('_', ' ')} ${count}`)
                .join(', ')}
            </Text>
          )}
          <Input
            id={`archive-bom-reason-${release.id}`}
            aria-label={`Archive reason for ${release.number}`}
            placeholder="Reason, e.g. Incorrect VMU BOM"
          />
        </Stack>
      ),
      handleConfirm: async () => {
        const reason = (
          document.getElementById(
            `archive-bom-reason-${release.id}`,
          ) as HTMLInputElement | null
        )?.value.trim();
        if (!reason) {
          toasterApi({
            type: 'warning',
            message: 'Archive reason is required.',
          });
          return;
        }
        await archiveRelease({
          releaseId: release.id,
          body: { reason },
        }).unwrap();
        toasterApi({
          type: 'success',
          message: `Archived BOM ${release.number}`,
        });
      },
    });
  };

  if (!project.id) {
    return <Text>Select a project to manage its BOM imports.</Text>;
  }

  return (
    <Stack gap={4}>
      <Box>
        <Text fontWeight="bold">BOM Imports</Text>
        <Text color="fg.muted" fontSize="sm">
          Active selectors use only the successfully parsed Active BOM.
          Archived and superseded imports remain available as history.
        </Text>
      </Box>

      <Card.Root>
        <Card.Body p={0} overflowX="auto">
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>File</Table.ColumnHeader>
                <Table.ColumnHeader>Release</Table.ColumnHeader>
                <Table.ColumnHeader>Status</Table.ColumnHeader>
                <Table.ColumnHeader>Source</Table.ColumnHeader>
                <Table.ColumnHeader>Uploaded</Table.ColumnHeader>
                <Table.ColumnHeader>Rows</Table.ColumnHeader>
                <Table.ColumnHeader>Actions</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {releases.map((release) => (
                <Table.Row key={release.id}>
                  <Table.Cell>
                    <Text fontWeight="medium">
                      {release.bom_file_name || 'BOM file'}
                    </Text>
                    {release.bom_status === 'FAILED' &&
                      release.bom_error_message && (
                        <Text color="red.fg" fontSize="xs">
                          {release.bom_error_message}
                        </Text>
                      )}
                  </Table.Cell>
                  <Table.Cell>{release.number}</Table.Cell>
                  <Table.Cell>
                    <Badge colorPalette={statusColor(release.bom_status)}>
                      {release.bom_status ?? 'PROCESSING'}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    {release.bom_source === 'REPLACEMENT'
                      ? 'Replacement'
                      : 'Upload'}
                  </Table.Cell>
                  <Table.Cell>
                    <Text>{release.uploaded_by_name || 'Unknown user'}</Text>
                    <Text color="fg.muted" fontSize="xs">
                      {new Date(release.created_at).toLocaleString()}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>{release.bom_row_count ?? 0}</Table.Cell>
                  <Table.Cell>
                    <Flex gap={2} flexWrap="wrap">
                      <PermissionCheck
                        permissions={[permissionsKeys.add_release]}
                      >
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={() => setReplacementFor(release)}
                        >
                          Upload replacement
                        </Button>
                      </PermissionCheck>
                      {release.bom_status !== 'ARCHIVED' && (
                        <PermissionCheck
                          permissions={[permissionsKeys.delete_release]}
                        >
                          <Button
                            size="xs"
                            colorPalette="red"
                            variant="outline"
                            loading={isArchiving}
                            onClick={() => archive(release)}
                          >
                            Archive
                          </Button>
                        </PermissionCheck>
                      )}
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ))}
              {!isLoading && releases.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={7}>
                    <Text textAlign="center" color="fg.muted" py={4}>
                      No BOM imports for this project.
                    </Text>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table.Root>
        </Card.Body>
      </Card.Root>

      {replacementFor && (
        <Card.Root borderColor="orange.border" borderWidth="1px">
          <Card.Body>
            <Flex justify="space-between" align="center" mb={3}>
              <Text fontWeight="bold">
                Replacement for {replacementFor.number}
              </Text>
              <Button
                size="xs"
                variant="ghost"
                onClick={() => setReplacementFor(null)}
              >
                Cancel replacement
              </Button>
            </Flex>
            <ManageCard
              isNew
              replacementFor={replacementFor}
            />
          </Card.Body>
        </Card.Root>
      )}

      {audit.length > 0 && (
        <Card.Root>
          <Card.Body>
            <Text fontWeight="bold" mb={2}>
              Recent BOM history
            </Text>
            <Stack gap={1}>
              {audit.slice(0, 8).map((event) => (
                <Text key={event.id} fontSize="sm">
                  {event.action} · Release {event.release} ·{' '}
                  {event.actor_name || 'System'} · {event.reason || 'No reason'}
                </Text>
              ))}
            </Stack>
          </Card.Body>
        </Card.Root>
      )}
    </Stack>
  );
};

export default BomImports;
